'use client'

import type { ReactNode } from 'react'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  cn,
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
  Slider,
  TooltipIcon,
} from '@nwwp/ui-shadcn'
import { NumberInput } from '../../../controls/NumberInput'
import { fmtCurrency, parseCurrency } from '../../../utils/currency-format'
import type { CalculatorResults, CalculatorSetters, CalculatorState } from '../types'

const LABEL = 'text-body-sm-bold text-gray-darker'
const VALUE = 'text-body-sm-bold tabular-nums text-gray-darker'
const CARD =
  'overflow-hidden border border-border bg-card first:rounded-t-xl last:rounded-b-xl [&:not(:first-child)]:border-t-0'

function Pct({ value, digits = 1 }: { value: number; digits?: number }) {
  return <>{value.toFixed(digits)}%</>
}

function SliderField({
  label,
  value,
  min,
  max,
  step,
  onChange,
  display,
  hint,
  tooltip,
}: {
  label: string
  value: number
  min: number
  max: number
  step: number
  onChange: (value: number) => void
  display: ReactNode
  hint?: ReactNode
  tooltip?: string
}) {
  return (
    <Field>
      <div className="flex items-center justify-between gap-3">
        <FieldLabel className={LABEL}>
          {label}
          {tooltip && <TooltipIcon title={tooltip} aria-label={`${label} — more information`} />}
        </FieldLabel>
        <span className={VALUE}>{display}</span>
      </div>
      <Slider
        aria-label={label}
        min={min}
        max={max}
        step={step}
        value={[value]}
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        onValueChange={(values) => onChange(values[0]!)}
      />
      {hint && <FieldDescription className="text-subtitle text-neutral-dark">{hint}</FieldDescription>}
    </Field>
  )
}

function MoneyField({
  label,
  value,
  onChange,
  hint,
  hintClassName,
  tooltip,
}: {
  label: string
  value: number
  onChange: (value: number) => void
  hint?: string
  hintClassName?: string
  tooltip?: string
}) {
  return (
    <Field>
      <FieldLabel className={LABEL}>
        {label}
        {tooltip && <TooltipIcon title={tooltip} aria-label={`${label} — more information`} />}
      </FieldLabel>
      {hint && (
        <FieldDescription className={cn(hintClassName || 'text-subtitle', 'text-neutral-dark')}>
          {hint}
        </FieldDescription>
      )}
      <NumberInput
        aria-label={label}
        prefix="$"
        value={value}
        decimalScale={0}
        allowNegative={false}
        onChange={(e) => onChange(parseCurrency(e.target.value))}
        className="font-semibold text-gray-darker"
      />
    </Field>
  )
}

function Section({
  value,
  step,
  title,
  summary,
  children,
}: {
  value: string
  step: number
  title: ReactNode
  summary?: string
  children: ReactNode
}) {
  return (
    <AccordionItem value={value} className={CARD}>
      <AccordionTrigger className="p-4 hover:no-underline tablet:px-6">
        <span className="flex items-center gap-3">
          <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-green-darker text-body-sm-bold text-white">
            {step}
          </span>
          <span className="text-title-xs text-gray-darker">{title}</span>
        </span>
        {summary && (
          <span className="ml-auto hidden pr-3 text-body-sm-bold text-neutral-dark tablet:block">{summary}</span>
        )}
      </AccordionTrigger>
      <AccordionContent className="px-4 pb-5 tablet:px-6">
        <FieldGroup className="gap-5">{children}</FieldGroup>
      </AccordionContent>
    </AccordionItem>
  )
}

interface InputsPanelProps {
  state: CalculatorState
  setters: CalculatorSetters
  results: CalculatorResults
}

export function InputsPanel({ state, setters, results }: InputsPanelProps) {
  const downPaymentHint = `${fmtCurrency(results.downPayment)} down`

  return (
    <Accordion type="multiple" defaultValue={['upfront']} className="w-full">
      <Section value="upfront" step={1} title={<>Upfront costs</>} summary={fmtCurrency(results.totalUpfront)}>
        <SliderField
          label="How Long Do You Plan to Own?"
          value={state.horizonYears}
          min={1}
          max={30}
          step={1}
          onChange={setters.horizonYears}
          display={`${state.horizonYears} yr`}
          tooltip="How many years you plan to live in the home before selling. Longer horizons tend to favor buying."
        />
        <MoneyField label="Purchase Price" value={state.purchasePrice} onChange={setters.purchasePrice} />
        <SliderField
          label="Down Payment"
          value={state.downPaymentPct}
          min={0}
          max={50}
          step={1}
          onChange={setters.downPaymentPct}
          display={`${state.downPaymentPct}%`}
          hint={downPaymentHint}
        />
        <MoneyField
          label="Home Inspection"
          value={state.homeInspection}
          onChange={setters.homeInspection}
          hint="National avg: $300-$500 for a standard single-family home"
          hintClassName="text-body-xs"
        />
        <MoneyField
          label="Initial Repairs / Renovation"
          value={state.initialRepairs}
          onChange={setters.initialRepairs}
        />
        <MoneyField
          label="Moving Expenses"
          value={state.movingExpenses}
          onChange={setters.movingExpenses}
          hint="National avg: $1,250 for local moves; $4,800+ for long-distance"
          hintClassName="text-body-xs"
        />
        <p className="border-t border-border pt-3 text-subtitle-sm font-bold tracking-widest text-neutral-dark uppercase">
          Closing Costs
        </p>
        <SliderField
          label="Closing Costs (% of purchase price)"
          value={state.closingCostsPct}
          min={0}
          max={10}
          step={0.5}
          onChange={setters.closingCostsPct}
          display={<Pct value={state.closingCostsPct} />}
          tooltip="Includes loan origination, appraisal, title insurance, surveys, taxes, and recording fees. Typically 2-6% of the purchase price."
        />
      </Section>

      <Section
        value="ongoing"
        step={2}
        title={<>Ongoing costs</>}
        summary={`${fmtCurrency(results.totalMortgagePaid + results.propertyTaxAndInsurance + results.totalPmi + results.maintenanceHoaOther + results.totalUtilities)}/${state.horizonYears}yr`}
      >
        <p className="text-subtitle-sm font-bold tracking-widest text-neutral-dark uppercase">Mortgage</p>
        <div className="flex items-center justify-between gap-3">
          <FieldLabel className={cn(LABEL, 'flex items-center')}>
            Mortgage Amount
            <TooltipIcon title="Automatically calculated as purchase price minus down payment." />
          </FieldLabel>
          <span className={VALUE}>{fmtCurrency(results.loanAmount)}</span>
        </div>
        <SliderField
          label="Interest Rate"
          value={state.interestRate}
          min={1}
          max={12}
          step={0.125}
          onChange={setters.interestRate}
          display={<Pct value={state.interestRate} digits={2} />}
        />
        <SliderField
          label="Loan Term"
          value={state.loanTermYears}
          min={10}
          max={30}
          step={5}
          onChange={setters.loanTermYears}
          display={`${state.loanTermYears} yr`}
        />
        <p className="border-t border-border pt-3 text-subtitle-sm font-bold tracking-widest text-neutral-dark uppercase">
          Annual Costs
        </p>
        <SliderField
          label="Property Tax"
          value={state.propertyTaxPct}
          min={0}
          max={5}
          step={0.1}
          onChange={setters.propertyTaxPct}
          display={<Pct value={state.propertyTaxPct} digits={2} />}
        />
        <SliderField
          label="Homeowner Insurance"
          value={state.homeownerInsurancePct}
          min={0}
          max={3}
          step={0.01}
          onChange={setters.homeownerInsurancePct}
          display={<Pct value={state.homeownerInsurancePct} digits={2} />}
        />
        <SliderField
          label="PMI"
          value={state.pmiPct}
          min={0}
          max={2}
          step={0.05}
          onChange={setters.pmiPct}
          display={<Pct value={state.pmiPct} digits={2} />}
          tooltip="Private Mortgage Insurance is usually required if your down payment is less than 20%."
        />
        <MoneyField label="HOA / Condo Fees (annual)" value={state.hoaAnnual} onChange={setters.hoaAnnual} />
        <SliderField
          label="Maintenance + Repairs"
          value={state.maintenancePct}
          min={0}
          max={4}
          step={0.1}
          onChange={setters.maintenancePct}
          display={<Pct value={state.maintenancePct} digits={2} />}
          tooltip="A common rule of thumb is 1% of the home's value per year for routine maintenance. Older homes or larger properties may need 2-4%."
        />
        <MoneyField label="Landscaping / Yard (annual)" value={state.yardAnnual} onChange={setters.yardAnnual} />
        <MoneyField label="Home Security (annual)" value={state.securityAnnual} onChange={setters.securityAnnual} />
        <p className="border-t border-border pt-3 text-subtitle-sm font-bold tracking-widest text-neutral-dark uppercase">
          Utilities
        </p>
        <MoneyField
          label="Total Utilities (annual)"
          value={state.utilitiesAnnual}
          onChange={setters.utilitiesAnnual}
          tooltip="Includes water, electricity, gas, trash, and sewer. The national average for a single-family home is roughly $1,400-$1,600 per year."
        />
      </Section>

      <Section value="repairs" step={3} title={<>Major repairs + upgrades</>} summary={fmtCurrency(state.majorRepairs)}>
        <FieldDescription className="text-body-sm leading-relaxed text-neutral-dark">
          Enter the total you expect to spend on major repairs and upgrades during your ownership period. Common costs
          include roof replacement ($9,500-$12,000), HVAC ($5,000-$12,000), kitchen remodel ($15,000-$30,000), and
          bathroom upgrades ($12,000-$15,000 each). Not every homeowner will need these — leave at $0 if not applicable.
        </FieldDescription>
        <MoneyField label="Total Major Repairs + Upgrades" value={state.majorRepairs} onChange={setters.majorRepairs} />
      </Section>

      <Section value="exit" step={4} title={<>Exit costs</>} summary={`Equity: ${fmtCurrency(results.netEquity)}`}>
        <SliderField
          label="Property Appreciation"
          value={state.appreciationPct}
          min={0}
          max={8}
          step={0.25}
          onChange={setters.appreciationPct}
          display={<Pct value={state.appreciationPct} />}
          tooltip="Average annual appreciation of your home's value. The national historical average is roughly 3-4%."
        />
        <SliderField
          label="Agent Commission"
          value={state.agentCommissionPct}
          min={0}
          max={8}
          step={0.5}
          onChange={setters.agentCommissionPct}
          display={<Pct value={state.agentCommissionPct} />}
        />
        <SliderField
          label="Capital Gains Tax"
          value={state.capitalGainsTaxPct}
          min={0}
          max={30}
          step={1}
          onChange={setters.capitalGainsTaxPct}
          display={`${Math.round(state.capitalGainsTaxPct)}%`}
          tooltip="Tax on profit from the sale. Many homeowners qualify for a $250K/$500K exclusion. This rate applies to gains above the exclusion."
        />
        <MoneyField label="Staging + Home Prep" value={state.stagingCost} onChange={setters.stagingCost} />
      </Section>

      <Section value="rent" step={5} title={<>Renting alternative</>} summary={fmtCurrency(results.totalRentNetCost)}>
        <MoneyField label="Monthly Rent" value={state.monthlyRent} onChange={setters.monthlyRent} />
        <SliderField
          label="Renter's Insurance"
          value={state.rentersInsurancePct}
          min={0}
          max={6}
          step={0.25}
          onChange={setters.rentersInsurancePct}
          display={<Pct value={state.rentersInsurancePct} />}
        />
        <SliderField
          label="Rent Appreciation"
          value={state.rentAppreciationPct}
          min={0}
          max={8}
          step={0.25}
          onChange={setters.rentAppreciationPct}
          display={<Pct value={state.rentAppreciationPct} />}
        />
        <SliderField
          label="Return on Saved Money"
          value={state.investmentReturnPct}
          min={0}
          max={14}
          step={0.5}
          onChange={setters.investmentReturnPct}
          display={<Pct value={state.investmentReturnPct} />}
          tooltip="If you rent instead of buying, your down payment and the monthly difference can be invested. This is the assumed annual return on that capital."
        />
      </Section>
    </Accordion>
  )
}
